import { ClassNames, css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import type { TooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import useControlled from '@mui/material/utils/useControlled';
import type { HTMLAttributes, PointerEvent } from 'react';
import { cloneElement } from 'react';

import { su, theme, typography } from '@adflow/frontend/ui-theme';

import { DropdownTransition } from './DropdownTransition';

const hubTooltipStyles = {
	tooltip: css(typography.regular, su.roundedLg, {
		backgroundColor: theme.colors.white,
		border: '1px solid #d1fcbc',
		boxShadow: '0px 4px 12px #0000001f',

		padding: '0.75rem',
		whiteSpace: 'normal',
	}),
	arrow: css({
		'&::before': {
			backgroundColor: theme.colors.white,
			border: '1px solid #d1fcbc',
			// No shadow on the arrow. It would double up with the tooltip's shadow
			// and look bad.
		},
	}),
} satisfies Partial<Record<keyof TooltipClasses, CSSInterpolation>>;

export interface HubTooltipProps extends TooltipProps {
	styles?: { popper?: CSSInterpolation; tooltip?: CSSInterpolation };
}

export function HubTooltip({
	styles,
	children,
	...forwardedProps
}: HubTooltipProps) {
	const [open, setOpen] = useControlled({
		controlled: forwardedProps.open,
		default: false,
		name: 'HubTooltip',
		state: 'open',
	});

	const handleClose = () => {
		setOpen(false);
	};

	const handleOpen = () => {
		setOpen(true);
	};

	const childrenProps: HTMLAttributes<HTMLElement> = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		...(children.props as any),
	};

	{
		// WORKAROUND: Open tooltip on click. `enterTouchDelay={0}` does not always
		//             open the tooltip on a fast touch.
		//
		// `handleClose` will be called when MUI's tooltip handles the `mouseleave`
		// event.
		const originalChildrenPropsOnClick = childrenProps.onClick;
		childrenProps.onClick = (event) => {
			originalChildrenPropsOnClick?.(event);
			handleOpen();
		};
	}

	return (
		<ClassNames>
			{
				// eslint-disable-next-line @typescript-eslint/no-shadow
				({ css }) => (
					<ClickAwayListener onClickAway={handleClose}>
						<Tooltip
							arrow
							TransitionComponent={DropdownTransition}
							{...forwardedProps}
							// WORKAROUND: We don't use the built in touch behavior. It feels
							//             janky. We use on click instead.
							disableTouchListener
							open={open}
							onOpen={handleOpen}
							onClose={handleClose}
							slotProps={{
								...forwardedProps.slotProps,
								popper: {
									...forwardedProps.slotProps?.popper,
									// On mobile, the default margin is too large. Make it consistent with desktop.
									sx: {
										[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
											{ marginTop: '12px' },
										[`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
											{ marginBottom: '12px' },
										[`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
											{ marginLeft: '12px' },
										[`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
											{ marginRight: '12px' },
									},
								},
								tooltip: {
									...forwardedProps.slotProps?.tooltip,
									// WORKAROUND: This is to prevent the tooltip from closing when
									//             touching on the tooltip. This is only an issue on
									//             touch devices. Mouse device work fine.
									onPointerDown: (e: PointerEvent) => {
										// Prevents a mouseleave event from firing on the trigger
										// element when the tooltip is touched.
										e.preventDefault();
									},
								},
							}}
							classes={{
								popper: css(styles?.popper),
								tooltip: css(hubTooltipStyles.tooltip, styles?.tooltip),
								arrow: css(hubTooltipStyles.arrow),
							}}
						>
							{cloneElement(children, childrenProps)}
						</Tooltip>
					</ClickAwayListener>
				)
			}
		</ClassNames>
	);
}
